import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  postV1CompaniesCompanyIdDriversGeofenceTriggers,
  postV1CompaniesCompanyIdEquipmentGeofenceTriggers,
} from '@treadinc/horizon-api-spec';
import { convertLength } from '@turf/helpers';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useState } from 'react';

import { GeofenceTrigger } from '~hooks/useGeofenceTriggers/models';
import { Job } from '~hooks/useJob/models';
import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';
import { hexToRgba } from '~utils/utilFunctions';

import GeofenceConfiguration, {
  FilterByType,
  GeofenceConfigurationFormState,
  GeofenceType,
} from './GeofenceConfiguration';
import GeofenceResultsPanel from './GeofenceResultsPanel';

interface GeofenceReportsProps {
  onLocationChange?: (coordinates: { lat: number; lng: number } | null) => void;
  onDrawingCoordinatesChange?: (
    coordinates: { lat: number; lng: number }[],
    isPolygonMode: boolean,
  ) => void;
  onCircleRadiusChange?: (radius: number | null) => void;
  geofenceRecenterPosition?: { lat: number; lng: number } | null;
  geofenceCircleRadius?: number | null;
  drawingCoordinates?: { lat: number; lng: number }[];
  isMapPickMode?: boolean;
  onPickLocationModeToggle?: () => void;
  pickedMapCoordinates?: { lat: number; lng: number } | null;
  onPickedMapCoordinatesProcessed?: () => void;
  onViewRoute?: (pings: LocationHistoryMinimal[], job?: Job) => void;
  onViewRouteLoadingChange?: (isLoading: boolean) => void;
}

export default function GeofenceReports({
  onLocationChange,
  onDrawingCoordinatesChange,
  onCircleRadiusChange,
  geofenceRecenterPosition,
  geofenceCircleRadius,
  drawingCoordinates,
  isMapPickMode,
  onPickLocationModeToggle,
  pickedMapCoordinates,
  onPickedMapCoordinatesProcessed,
  onViewRoute,
  onViewRouteLoadingChange,
}: GeofenceReportsProps) {
  const { userStore } = useStores();
  const isFeet = userStore?.userCompany?.isFeet ?? false;
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<GeofenceTrigger[] | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [resultsFilterBy, setResultsFilterBy] = useState<FilterByType>(
    FilterByType.DRIVER,
  );

  const handleProcess = async (formState: GeofenceConfigurationFormState) => {
    const companyId = getEffectiveUserCompanyId(userStore);

    if (!formState.startDate || !formState.endDate) {
      return;
    }

    // Clear previous results and route pings when starting a new search
    setResults(null);
    onViewRoute?.([]);

    const isPolygon = formState.geofenceType === GeofenceType.POLYGON;
    const isCircle = formState.geofenceType === GeofenceType.CIRCLE;

    // Build the geofence object based on type
    const geofence: {
      name: string;
      type: string;
      geojson?: {
        type: string;
        coordinates: number[][][];
      };
      circle_center?: {
        lat: number;
        lon: number;
      };
      circle_radius?: number;
    } = {
      name: formState.geofenceName,
      type: formState.geofenceType,
    };

    if (isPolygon && formState.drawingCoordinates.length > 0) {
      // Convert coordinates to GeoJSON format: [[[lng, lat], [lng, lat], ...]]
      // The polygon must be closed (first and last point must be the same)
      const coordinates = formState.drawingCoordinates.map((coord) => [
        coord.lng,
        coord.lat,
      ]);

      // Close the polygon by adding the first point at the end
      if (coordinates.length > 0) {
        coordinates.push(coordinates[0]);
      }

      geofence.geojson = {
        type: 'Polygon',
        coordinates: [coordinates],
      };
    } else if (isCircle && formState.circleRadius !== null) {
      // Use latLon for center when valid (updated by map drag); otherwise fall back to location (e.g. address select)
      const latLonParts = formState.latLon.split(',').map((s) => s.trim());
      const fromLatLon =
        latLonParts.length === 2 &&
        latLonParts.every((p) => p !== '' && !Number.isNaN(parseFloat(p)));
      if (fromLatLon) {
        geofence.circle_center = {
          lat: parseFloat(latLonParts[0]),
          lon: parseFloat(latLonParts[1]),
        };
      } else if (formState.location) {
        geofence.circle_center = {
          lat: formState.location.lat,
          lon: formState.location.lng,
        };
      }
      if (geofence.circle_center) {
        // Form stores radius in display units (feet or meters); convert to meters for API
        geofence.circle_radius = isFeet
          ? convertLength(formState.circleRadius, 'feet', 'meters')
          : formState.circleRadius;
      }
    }

    // For single date mode, startDate is start of day and endDate is end of day
    const body = {
      start_at: dayjs.tz(formState.startDate).startOf('day').toISOString(),
      end_at: dayjs.tz(formState.endDate).endOf('day').toISOString(),
      geofences: [{ geofence }],
    };

    try {
      setIsProcessing(true);

      const apiCall =
        formState.filterBy === FilterByType.DRIVER
          ? postV1CompaniesCompanyIdDriversGeofenceTriggers
          : postV1CompaniesCompanyIdEquipmentGeofenceTriggers;

      const response = await apiCall({ path: { 'company-id': companyId }, body });

      const geofenceTriggers = response.data.data.map(GeofenceTrigger.parse);
      setResults(geofenceTriggers);
      setHasSearched(true);
      setResultsFilterBy(formState.filterBy);
    } catch (e) {
      connection.handleRequestError(
        e,
        t('error_messages.geofence_triggers.failed_to_create'),
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseResults = () => {
    setResults(null);
    onViewRoute?.([]);
  };

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}
    >
      <GeofenceConfiguration
        drawingCoordinates={drawingCoordinates}
        geofenceRecenterPosition={geofenceRecenterPosition}
        geofenceCircleRadius={geofenceCircleRadius}
        isProcessing={isProcessing}
        onCircleRadiusChange={onCircleRadiusChange}
        onDrawingCoordinatesChange={onDrawingCoordinatesChange}
        onLocationChange={onLocationChange}
        onProcess={handleProcess}
        isMapPickMode={isMapPickMode}
        onPickLocationModeToggle={onPickLocationModeToggle}
        pickedMapCoordinates={pickedMapCoordinates}
        onPickedMapCoordinatesProcessed={onPickedMapCoordinatesProcessed}
      />

      {results && results.length > 0 && (
        <GeofenceResultsPanel
          filterBy={resultsFilterBy}
          onClose={handleCloseResults}
          onViewRoute={onViewRoute}
          onViewRouteLoadingChange={onViewRouteLoadingChange}
          triggers={results}
        />
      )}

      {hasSearched && results && results.length === 0 && (
        <Box
          sx={(theme) => ({
            alignItems: 'center',
            bgcolor: hexToRgba(theme.brandV2.colors.treadOrangeDark, 0.1),
            borderRadius: theme.brandV2.borderRadius,
            display: 'flex',
            gap: 0.75,
            justifyContent: 'center',
            m: 2,
            p: 2,
          })}
        >
          <InfoOutlined
            sx={(theme) => ({
              color: theme.brandV2.colors.treadOrangeDark,
              fontSize: '18px',
            })}
          />

          <Typography
            variant="body2"
            sx={(theme) => ({ color: theme.brandV2.colors.treadOrangeDark })}
          >
            {t('common.no_results')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
